// src/lib/lead/extractionScheduler.ts
export type ExtractScheduleArgs = { leadId: string; transcript: string };

export type ExtractScheduler = {
  schedule: (leadId: string, transcript: string) => void;
  flush: () => Promise<void>;
  cancel: () => void;
  getInFlight: () => Promise<void> | null;
};

export type CreateExtractSchedulerArgs = {
  runExtractionNow: (leadId: string, transcript: string) => Promise<void>;
  debounceMs?: number; // default 800
};

export function createExtractScheduler({
  runExtractionNow,
  debounceMs = 800,
}: CreateExtractSchedulerArgs): ExtractScheduler {
  let timer: number | null = null;
  let latest: ExtractScheduleArgs | null = null;
  let inFlight: Promise<void> | null = null;

  const cancel = () => {
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (leadId: string, transcript: string) => {
    latest = { leadId, transcript };
    cancel();

    timer = window.setTimeout(() => {
      timer = null;

      const p = runExtractionNow(leadId, transcript);
      inFlight = p;

      p.finally(() => {
        if (inFlight === p) inFlight = null;
      });
    }, debounceMs);
  };

  const flush = async () => {
    cancel();
    if (!latest) return;

    const text = (latest.transcript || "").trim();
    if (!text) return;

    const p = runExtractionNow(latest.leadId, text);
    inFlight = p;

    try {
      await p;
    } finally {
      if (inFlight === p) inFlight = null;
    }

    if (inFlight) await inFlight;
  };

  return { schedule, flush, cancel, getInFlight: () => inFlight };
}